
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import { useEffect } from '@storybook/client-api';
import { createElasticTextArea } from '../input/elastic-textarea.ts';
import { makeTwigInclude } from '../../make-twig-include.js';
import template from './comment-form.twig';
const tyler = {
  name: 'Tyler Sticka',
  link: 'https://cloudfour.com/is/tyler',
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Comment Form" args={{
      isLoggedIn: false,
      isReply: false
    }} argTypes={{
      isLoggedIn: {
        type: {
          name: 'boolean'
        }
      },
      isReply: {
        type: {
          name: 'boolean'
        }
      }
    }} mdxType="Meta" />
    <h1>{`Comment Form`}</h1>
    <p>{`The form used to add a comment to an article. Including the `}<inlineCode parentName="p">{`heading_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`help_text_id`}</inlineCode>{` properties will provide assistive technologies additional context.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Default" args={{
        isReply: false
      }} parameters={{
        docs: {
          source: {
            code: makeTwigInclude('@cloudfour/components/comment-form/comment-form.twig', {
              heading_id: 'leave-a-comment',
              help_text_id: 'leave-a-comment-help-text'
            })
          }
        }
      }} mdxType="Story">
    {({
          isLoggedIn,
          isReply
        }) => {
          useEffect(() => {
            const {
              destroy
            } = createElasticTextArea(document.querySelector('.js-elastic-textarea'));
            return destroy;
          }, []);
          return template({
            logged_in_user: isLoggedIn ? tyler : undefined,
            log_out_url: '/logout',
            is_reply: isReply,
            heading_id: 'leave-a-comment',
            help_text_id: 'leave-a-comment-help-text'
          });
        }}
  </Story>
    </Canvas>
    <p>{`There is also a reply version of this component (`}<inlineCode parentName="p">{`is_reply: true`}</inlineCode>{`), used to reply to an existing comment. (You'll also want to modify `}<inlineCode parentName="p">{`heading_tag`}</inlineCode>{`, `}<inlineCode parentName="p">{`heading_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`heading_class`}</inlineCode>{`, `}<inlineCode parentName="p">{`heading_text`}</inlineCode>{`, and `}<inlineCode parentName="p">{`main_label`}</inlineCode>{`. When using the `}<a parentName="p" {...{
        "href": "/docs/components-comment--with-reply-button"
      }}>{`Comment pattern`}</a>{` this will be done automatically.)`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Reply" args={{
        isReply: true,
        isLoggedIn: true
      }} parameters={{
        docs: {
          source: {
            code: makeTwigInclude('@cloudfour/components/comment-form/comment-form.twig', {
              logged_in_user: tyler,
              log_out_url: '/logout',
              is_reply: true,
              heading_id: 'reply-to-comment-100',
              heading_tag: 'h4',
              heading_text: 'Reply to John Doe',
              heading_class: 'u-hidden-visually',
              main_label: 'Reply'
            })
          }
        }
      }} mdxType="Story">
    {({
          isLoggedIn,
          isReply
        }) => {
          useEffect(() => {
            const {
              destroy
            } = createElasticTextArea(document.querySelector('.js-elastic-textarea'));
            return destroy;
          }, []);
          return template({
            logged_in_user: isLoggedIn ? tyler : undefined,
            log_out_url: '/logout',
            is_reply: isReply,
            heading_id: 'reply-to-comment-100',
            heading_tag: 'h4',
            heading_text: 'Reply to John Doe',
            heading_class: 'u-hidden-visually',
            main_label: 'Reply'
          });
        }}
  </Story>
    </Canvas>
    <h2>{`Template Properties`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`class`}</inlineCode>{`: Append a class to the root element.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`heading_id`}</inlineCode>{`: A unique ID for your heading element. This will be used as the accessible name for the form.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`heading_tag`}</inlineCode>{`: The tag for your heading (defaults to `}<inlineCode parentName="p">{`h2`}</inlineCode>{`).`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`heading_text`}</inlineCode>{`: The text for your heading.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`heading_class`}</inlineCode>{`: An optional class for your heading.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`heading_id`}</inlineCode>{`: A unique ID for your help text. This will be used as the accessible description for the reply textarea.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`main_label`}</inlineCode>{`: The label for the primary message textarea. Defaults to `}<inlineCode parentName="p">{`message`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`logged_in_user`}</inlineCode>{`: `}<a parentName="p" {...{
            "href": "https://timber.github.io/docs/reference/timber-user/#properties"
          }}>{`user object`}</a>{` of the type:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-ts"
          }}>{`{
  name: string | () => string;
  link: string | () => string;
}
`}</code></pre>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`log_out_url`}</inlineCode>{`: URL used for log out link.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`is_reply`}</inlineCode>{`: Whether the comment is a reply to another comment.`}</p>
      </li>
    </ul>
    <h2>{`Template blocks`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`prompt`}</inlineCode>{`: The text that is displayed above the message box.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`hidden_inputs`}</inlineCode>{`: Can be used to include `}<inlineCode parentName="li">{`<input type="hidden">`}</inlineCode>{` elements, which systems like Wordpress use to provide additional context during form submission.`}</li>
    </ul>
    <h2>{`JavaScript`}</h2>
    <p>{`The Comment Form component includes the `}<a parentName="p" {...{
        "href": "/docs/components-input--elastic-textarea"
      }}>{`Elastic Textarea component`}</a>{`. See the Elastic Textarea component for JavaScript setup details.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const defaultStory = (({
  isLoggedIn,
  isReply
}) => {
  useEffect(() => {
    const {
      destroy
    } = createElasticTextArea(document.querySelector('.js-elastic-textarea'));
    return destroy;
  }, []);
  return template({
    logged_in_user: isLoggedIn ? tyler : undefined,
    log_out_url: '/logout',
    is_reply: isReply,
    heading_id: 'leave-a-comment',
    help_text_id: 'leave-a-comment-help-text'
  });
});
defaultStory.storyName = 'Default';
defaultStory.args = {
  isReply: false
};
defaultStory.parameters = { storySource: { source: '({\n  isLoggedIn,\n  isReply\n}) => {\n  useEffect(() => {\n    const {\n      destroy\n    } = createElasticTextArea(document.querySelector(\'.js-elastic-textarea\'));\n    return destroy;\n  }, []);\n  return template({\n    logged_in_user: isLoggedIn ? tyler : undefined,\n    log_out_url: \'/logout\',\n    is_reply: isReply,\n    heading_id: \'leave-a-comment\',\n    help_text_id: \'leave-a-comment-help-text\'\n  });\n}' }, ...{
  docs: {
    source: {
      code: makeTwigInclude('@cloudfour/components/comment-form/comment-form.twig', {
        heading_id: 'leave-a-comment',
        help_text_id: 'leave-a-comment-help-text'
      })
    }
  }
} };

export const reply = (({
  isLoggedIn,
  isReply
}) => {
  useEffect(() => {
    const {
      destroy
    } = createElasticTextArea(document.querySelector('.js-elastic-textarea'));
    return destroy;
  }, []);
  return template({
    logged_in_user: isLoggedIn ? tyler : undefined,
    log_out_url: '/logout',
    is_reply: isReply,
    heading_id: 'reply-to-comment-100',
    heading_tag: 'h4',
    heading_text: 'Reply to John Doe',
    heading_class: 'u-hidden-visually',
    main_label: 'Reply'
  });
});
reply.storyName = 'Reply';
reply.args = {
  isReply: true,
  isLoggedIn: true
};
reply.parameters = { storySource: { source: '({\n  isLoggedIn,\n  isReply\n}) => {\n  useEffect(() => {\n    const {\n      destroy\n    } = createElasticTextArea(document.querySelector(\'.js-elastic-textarea\'));\n    return destroy;\n  }, []);\n  return template({\n    logged_in_user: isLoggedIn ? tyler : undefined,\n    log_out_url: \'/logout\',\n    is_reply: isReply,\n    heading_id: \'reply-to-comment-100\',\n    heading_tag: \'h4\',\n    heading_text: \'Reply to John Doe\',\n    heading_class: \'u-hidden-visually\',\n    main_label: \'Reply\'\n  });\n}' }, ...{
  docs: {
    source: {
      code: makeTwigInclude('@cloudfour/components/comment-form/comment-form.twig', {
        logged_in_user: tyler,
        log_out_url: '/logout',
        is_reply: true,
        heading_id: 'reply-to-comment-100',
        heading_tag: 'h4',
        heading_text: 'Reply to John Doe',
        heading_class: 'u-hidden-visually',
        main_label: 'Reply'
      })
    }
  }
} };

const componentMeta = { title: 'Components/Comment Form', args: {
  isLoggedIn: false,
  isReply: false
}, argTypes: {
  isLoggedIn: {
    type: {
      name: 'boolean'
    }
  },
  isReply: {
    type: {
      name: 'boolean'
    }
  }
}, includeStories: ["defaultStory","reply"],  };

const mdxStoryNameToKey = {"Default":"defaultStory","Reply":"reply"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;